import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import Swal, { SweetAlertOptions } from 'sweetalert2';

/** Configuración base para todos los alerts: diseño consistente y moderno */
const ALERT_DEFAULT_CONFIG = {
  customClass: {
    popup: 'app-alert',
    title: 'app-alert-title',
    htmlContainer: 'app-alert-body',
    icon: 'app-alert-icon',
    confirmButton: 'app-alert-btn app-alert-btn-confirm',
    cancelButton: 'app-alert-btn app-alert-btn-cancel',
    actions: 'app-alert-actions'
  },
  buttonsStyling: false,
  width: '22rem',
  padding: '1.5rem 1.25rem',
  showClass: {
    popup: 'app-alert-show'
  },
  hideClass: {
    popup: 'app-alert-hide'
  }
};

@Injectable({
  providedIn: 'root'
})
export class AlertsService {

  private get confirmText(): string {
    return this.translate.instant('shares.alerts.acepto') || 'Accept';
  }

  private get cancelText(): string {
    return this.translate.instant('shares.alerts.cancelar') || 'Cancel';
  }

  private fire(options: SweetAlertOptions) {
    return Swal.fire({
      ...ALERT_DEFAULT_CONFIG,
      confirmButtonText: this.confirmText,
      cancelButtonText: this.cancelText,
      ...options
    });
  }

  constructor(private translate: TranslateService) { }

  alertError(msg: string): void {
    this.fire({
      title: msg,
      icon: 'error'
    });
  }

  alertSuccess(msg: string): void {
    this.fire({
      title: msg,
      icon: 'success'
    });
  }

  alertSuccessDismiss(msg: string): void {
    this.fire({
      position: 'top-end',
      icon: 'success',
      title: msg,
      showConfirmButton: false,
      timer: 1000,
      width: '18rem',
      toast: true
    });
  }

  alertSuccessDismissTiny(msg: string): void {
    this.fire({
      position: 'top-end',
      icon: 'success',
      title: msg,
      showConfirmButton: false,
      timer: 800,
      width: '18rem',
      toast: true
    });
  }

  alertWarning(item: string) {
    return this.fire({
      icon: 'warning',
      title: this.translate.instant('common.areYouSure'),
      text: this.translate.instant('common.confirmDelete', { item }),
      showCancelButton: true
    });
  }

  alertWarningRestore(item: string) {
    return this.fire({
      icon: 'warning',
      title: this.translate.instant('common.areYouSure'),
      text: this.translate.instant('common.confirmRestore', { item }),
      showCancelButton: true
    });
  }

  alertWarningText(text: string) {
    return this.fire({
      icon: 'warning',
      title: this.translate.instant('common.notice'),
      text
    });
  }

  confirmOut() {
    return this.fire({
      icon: 'question',
      text: this.translate.instant('shares.alerts.confirmOut'),
      showCancelButton: true
    }).then(val => (val as any).value ? true : false);
  }

  confirmOverWrite(info:any): Promise<boolean> {
    let fields = '';
    Object.entries(info).forEach(([key, value]) => {
      if (value === false) {
        fields += `<span class="app-alert-badge">${key}</span>`;
      }
    });

    const title = this.translate.instant('shares.alerts.confirmOverWriteTitle');
    const desc = this.translate.instant('shares.alerts.confirmOverWriteDesc');
    return this.fire({
      icon: 'question',
      title,
      html: `
        <p class="app-alert-desc">${desc}</p>
        <div class="app-alert-badges">${fields}</div>
      `,
      showCancelButton: true,
      width: '26rem'
    }).then(val => (val as any).value ? true : false);
  }

  existContact(info: { name: string; lastname: string }) {
    return this.fire({
      icon: 'warning',
      text: this.translate.instant('common.phoneAssignedTo', { name: info.name, lastname: info.lastname }),
      showCancelButton: true
    });
  }

  /** Confirmación al desactivar producto: dejará de estar disponible en todas las tiendas */
  confirmDisableProductInAllStores(): Promise<boolean> {
    const title = this.translate.instant('shares.alerts.confirmDisableProductAllStoresTitle');
    const text = this.translate.instant('shares.alerts.confirmDisableProductAllStoresText');
    return this.fire({
      icon: 'warning',
      title,
      text,
      showCancelButton: true
    }).then(val => (val as any).value === true);
  }
}
